import { logger } from '@ecosystem/newron-client-lib';
import { BRAND_OWNER_DELIMITER } from '../common/brandOwner.constants';
import { getBrandAndBrandOwner } from '../common/brandOwner.utils';
import { Context, Entity, FiltersValues, GroupedListBoxMenuItem, ListBoxMenuItem, SettingsValues } from '../types';
import { CorePageState } from './reducer';
import { InsightErrorType } from '../data/types';
import { InsightData } from '../content/collection/types';
import { getPageDsl } from '../client/queryClient/pageDsl';
import { queryClient } from './ReactQueryProvider';
import stringify from 'fast-json-stable-stringify';
import { contextClient } from '../client';
import { reformatCorePageSettings } from '../content/collection/utils';

const channelsSplitValue = 'segment_outlet_group_code';
const regionSplitValue = 'region_name';
const allowedGroupingUpdateValues = [channelsSplitValue, regionSplitValue];

export const checkIfCurrencyChanged = (Context: Context, state: CorePageState) => {
  const currentCurrency = Context.settings?.values?.currency_code?.[0]?.value;
  const previousCurrency = state?.contextState?.settings?.currency_code?.[0]?.value;
  return currentCurrency && previousCurrency && currentCurrency !== previousCurrency;
};

export const appendBrandOwnerLabel = (item: ListBoxMenuItem, isSubMenuIndented: boolean): string => {
  if (!isSubMenuIndented) {
    return '';
  }
  return `${BRAND_OWNER_DELIMITER}${item.brandOwner ?? null}`;
};

/**
 * Set the isDisabled attribute to true for all the selected brands/brand owner
 * @param allSelectedOptions all selected options (rows including fixed and limited rows) in key indicators
 * @param options
 */
export const updateOptionDisableState = (
  allSelectedOptions: string[],
  options: ListBoxMenuItem[] | GroupedListBoxMenuItem[]
) => {
  const selectedBrandsAndBrandOwners = allSelectedOptions
    ?.filter(opt => opt.includes(BRAND_OWNER_DELIMITER))
    .map(getBrandAndBrandOwner);
  disableSelectedBrandOwners(selectedBrandsAndBrandOwners, options);
  disableSelectedIndependentAndOwnedBrands(selectedBrandsAndBrandOwners, options);
  return options;
};

/**
 * Set the isDisabled attribute to true if the selected brand owners exist in the formatted options list
 * @param selectedBrandsAndBrandOwners list of selected brand owners in key indicators
 * @param brandOptions list of brand options
 */
export const disableSelectedBrandOwners = (
  selectedBrandsAndBrandOwners: { brand: string; brandOwner: string }[],
  brandOptions: ListBoxMenuItem[] | GroupedListBoxMenuItem[]
) => {
  const selectedBrandOwners = selectedBrandsAndBrandOwners
    ?.filter(item => (item.brandOwner && item.brand === null) || item.brandOwner === item.brand)
    .map(item => item.brandOwner);

  const setIsDisabledSelectedBrandOwners = (opt: ListBoxMenuItem) => {
    if (selectedBrandOwners?.includes(opt.label)) {
      opt.isDisabled = true;
    } else {
      opt.isDisabled = false;
    }
  };
  updateBrandOptionsProperty(brandOptions, setIsDisabledSelectedBrandOwners);
};

/**
 * Set the isDisabled attribute for both independent brands and owned brands
 * @param selectedBrandsAndBrandOwners list of selected brand owners in key indicators
 * @param brandOptions list of brand options
 */
export const disableSelectedIndependentAndOwnedBrands = (
  selectedBrandsAndBrandOwners: { brand: string; brandOwner: string }[],
  brandOptions: ListBoxMenuItem[] | GroupedListBoxMenuItem[]
) => {
  const selectedIndependentBrands = selectedBrandsAndBrandOwners
    ?.filter(item => item.brand && item.brandOwner === null)
    .map(item => item.brand);

  const selectedOwnedBrands = selectedBrandsAndBrandOwners?.filter(item => item.brand && item.brandOwner);

  const setIsDisabledForBrands = (opt: ListBoxMenuItem) => {
    if (!Array.isArray(opt.value)) {
      // Handle independent brands - check both independent and owned brand selections
      const isIndependentBrandSelected = selectedIndependentBrands?.includes(opt.label);
      const isSharedOwnedBrandSelected = selectedOwnedBrands.some(ownedBrand => ownedBrand.brand?.includes(opt.label));

      opt.isDisabled = isIndependentBrandSelected || isSharedOwnedBrandSelected;
    } else {
      // Handle brand owner with owned brands
      opt.value.forEach(ownedBrands => {
        const ownedBrandItem = ownedBrands as ListBoxMenuItem;
        const isIndependentBrandMatched = selectedIndependentBrands.some(selected => selected === ownedBrandItem.value);
        const isOwnedBrandMatched = selectedOwnedBrands.some(selected => selected.brand === ownedBrandItem.value);

        ownedBrandItem.isDisabled = isIndependentBrandMatched || isOwnedBrandMatched;
      });
    }
  };

  updateBrandOptionsProperty(brandOptions, setIsDisabledForBrands);
};

/**
 * Function to update a property in brand options using the setProperty function
 * @param brandOptions brand options list
 * @param setProperty set property function
 */
const updateBrandOptionsProperty = (
  brandOptions: ListBoxMenuItem[] | GroupedListBoxMenuItem[],
  setProperty: (opt: ListBoxMenuItem) => void
) => {
  const isGrouped = brandOptions[0]?.type === 'MenuItemGrouped';
  if (isGrouped) {
    (brandOptions as GroupedListBoxMenuItem[]).forEach(group => ({
      ...group,
      list: group.list.forEach(setProperty),
    }));
  } else {
    (brandOptions as ListBoxMenuItem[]).forEach(setProperty);
  }
};

/**
 * Get the isLive value for the provider.
 * This value is used is determine the current page
 * where isLive will only have value when the page is a Collection page.
 * @param isLiveCollection boolean indicating if the collection is live or static
 * @returns An array with a single object containing the isLive value and label
 */
export const getIsLiveValueForProvider = (isLiveCollection: boolean) => {
  let value = [];
  const isCollectionPage = window?.location?.pathname.includes('/collections/');
  if (isCollectionPage) {
    value = [{ value: isLiveCollection.toString(), label: 'Live' }];
  }
  return value;
};

export const handleInsightDataChange = async ({ dsl, oldDsl, collection, dispatch }) => {
  const { insightData } = collection;
  const reformatInsightData = reformatCorePageSettings(insightData);
  const { paramsForData, componentSettings, paramsForContext, corePageSettings } = reformatInsightData ?? {};
  const headerMenus = getMenusFromSections(dsl?.sections || []);
  const updatedParams: { grouping?: string[]; sgSegment?: string[] } = {};
  const brandParams: { highlightedBrand?: Entity[] } = {};

  // --- Helper to validate DSL and update params ---
  const validateDslAndParams = async () => {
    if (headerMenus?.length) {
      if (!oldDsl && insightData?.multiEditConfig && paramsForContext) {
        oldDsl = await getOldPageDsl({
          ...insightData,
          paramsForContext: { ...paramsForContext, cell: insightData.multiEditConfig.oldCell },
        });
      }

      const oldDslHeaderMenus = oldDsl ? getMenusFromSections(oldDsl?.sections || []) : [];

      // Validate grouping
      getPageGroupingInfo({ paramsForData, oldDslHeaderMenus, headerMenus, updatedParams });
      // Validate sgSegment
      getPageSgSegmentInfo({ paramsForData, oldDslHeaderMenus, headerMenus, updatedParams });
    } else {
      const error = getPageDslErrors(dsl?.sections || []);

      // If error from pageDsl, skip data fetch call
      if (error) {
        collection?.onError({
          errorType: InsightErrorType.MULTI_CONFIG_ERROR,
          loggerMessage: 'PAGE_DSL_FETCH_FAILED',
          userMessage: error,
        });

        return false;
      }
    }

    return true;
  };

  if (insightData?.multiEditConfig) {
    const settings = await fetchContextSettings({ corePageSettings, paramsForContext });
    brandParams.highlightedBrand = settings?.values?.highlightedBrand ?? [];
  }

  const isValidDsl = await validateDslAndParams();
  if (!isValidDsl) return;

  const hasUpdates = Object.keys(updatedParams).length > 0 || Object.keys(brandParams).length > 0;

  if (hasUpdates) {
    const highlightedStrings = brandParams.highlightedBrand?.map(hb => `${hb.value}|${hb.label}`) ?? [];
    const highlightedValues = brandParams.highlightedBrand?.map(hb => hb.value) ?? [];

    const newData = {
      componentSettings: { ...componentSettings, ...updatedParams },
      paramsForContext: {
        ...paramsForContext,
        ...(highlightedStrings.length ? { highlightedBrand: highlightedStrings } : {}),
      },
      paramsForData: {
        ...paramsForData,
        ...updatedParams,
        ...(highlightedValues.length ? { highlightedBrand: highlightedValues } : {}),
      },
    };

    delete insightData.multiEditConfig;
    await collection.onDataChanged?.({ ...insightData, ...newData });
  }

  dispatch({ type: 'SET_IS_DSL_LOADED_FOR_COLLECTION', payload: true });
};

const getOldPageDsl = async (insightData: InsightData) => {
  const collectionsContext = insightData?.paramsForContext
    ? getCollectionsContext({ params: insightData?.paramsForContext })
    : null;

  if (!collectionsContext) return null;

  const {
    page: { url: pageUrl },
  } = insightData?.corePageSettings ?? { page: {} };

  const dslParams = {
    contextParams: {
      ...(collectionsContext?.settings ?? {}),
    },
    filterParams: collectionsContext?.filters ?? {},
    pageParams: {},
  };

  const dsl = await getPageDsl(pageUrl, dslParams);

  return dsl;
};

export const getMenusFromSections = sections => {
  try {
    if (!Array.isArray(sections)) {
      throw new Error('Input is not a valid array.');
    }

    // Step 1: Access the second item (index 1) and ensure it has `columns`
    const columnContainer = (sections ?? []).find(item => item.type === 'column');
    if (!columnContainer || columnContainer.type !== 'column' || !Array.isArray(columnContainer.columns)) {
      throw new Error("Missing or invalid 'columns' in second array element.");
    }

    const firstColumn = columnContainer.columns[0];
    if (!firstColumn?.component?.heading) {
      throw new Error("Missing 'component.heading' in first column.");
    }

    const titleArray = firstColumn.component.heading.title;
    if (!Array.isArray(titleArray)) {
      throw new Error("'component.heading.title' is not an array.");
    }

    // Step 2: Filter only objects that contain a 'menu' key
    const menuObjects = titleArray
      .filter(item => typeof item === 'object' && item !== null && 'menu' in item)
      .map(item => item.menu);

    return menuObjects;
  } catch (error) {
    logger.error(new Error(`Error extracting menu items: ${error.message}`));
    return [];
  }
};

export const getPageDslErrors = sections => {
  try {
    if (!Array.isArray(sections)) {
      throw new Error('Input is not a valid array.');
    }

    // Step 1: Access the second item (index 1) and ensure it has `columns`
    const columnContainer = (sections ?? []).find(item => item.type === 'column');
    if (!columnContainer || columnContainer.type !== 'column' || !Array.isArray(columnContainer.columns)) {
      throw new Error("Missing or invalid 'columns' in second array element.");
    }

    const firstColumn = columnContainer.columns[0];
    if (firstColumn?.component?.contents) {
      const fixed = firstColumn?.component?.contents?.fixed ?? [];
      return fixed?.length ? fixed[0]?.instructions?.message?.instruction : null;
    }
    return null;
  } catch (error) {
    logger.error(new Error(`Error extracting dsl erros: ${error.message}`));
    return null;
  }
};

const hasAllowedValue = (currentGroupingValue: string) => {
  return currentGroupingValue?.split('|').some(value => allowedGroupingUpdateValues.includes(value));
};

export const getPageGroupingInfo = ({ paramsForData, updatedParams, headerMenus, oldDslHeaderMenus }) => {
  const groupingLabel = 'grouping';
  const groupingMenu = headerMenus.find(item => item.paramName === groupingLabel);
  const oldGroupingMenu = oldDslHeaderMenus.find(item => item.paramName === groupingLabel);
  const currentGroupingValue = paramsForData?.grouping?.[0];

  if (!groupingMenu || !currentGroupingValue || !hasAllowedValue(currentGroupingValue)) return;
  let matchedLabel: string | null = null;

  // Step 1: Get the label from oldGroupingMenu using current value
  if (oldGroupingMenu) {
    const matchedOldItem = oldGroupingMenu.items.find(item => item.value === currentGroupingValue);
    matchedLabel = matchedOldItem?.label ?? null;
  }

  // Step 2: Find corresponding item in new groupingMenu by label
  const matchedNewItem = matchedLabel ? groupingMenu.items.find(item => item.label === matchedLabel) : null;

  if (matchedNewItem?.value === currentGroupingValue) return;

  if (matchedNewItem) updatedParams.grouping = [matchedNewItem.value];
  else {
    // Step 3: If label not found, validate current value directly against new menu
    const isValid = groupingMenu.items.some(item => item.value === currentGroupingValue);
    if (!isValid) {
      // Default to first available value
      updatedParams.grouping = [groupingMenu.items[0]?.value];
    }
  }
};

export const getPageSgSegmentInfo = ({ paramsForData, updatedParams, headerMenus, oldDslHeaderMenus }) => {
  const sgSegmentLabel = 'sgSegment';
  const channelsLabel = 'Channels';
  const regionsLabel = 'Regions';

  const segmentMenu = headerMenus.find(menu => menu.paramName === sgSegmentLabel);
  const oldSegmentMenu = oldDslHeaderMenus.find(menu => menu.paramName === sgSegmentLabel);
  const currentSegmentValue = paramsForData?.sgSegment?.[0];

  if (!segmentMenu || !currentSegmentValue || !hasAllowedValue(currentSegmentValue)) return;

  const selectedSectionLabel = getSelectedSectionLabel(currentSegmentValue, channelsLabel, regionsLabel);
  if (!selectedSectionLabel) return;

  const newMenu = segmentMenu.items.find(item => item.label === selectedSectionLabel);
  const oldMenu = oldSegmentMenu?.items.find(item => item.label === selectedSectionLabel);

  const matchedLabel = getMatchedLabel(oldMenu, currentSegmentValue);
  if (tryUpdateMatchedSegment(matchedLabel, newMenu, currentSegmentValue, updatedParams)) return;

  ensureValidSegment(newMenu, currentSegmentValue, updatedParams);
};

// --- helpers ---

const getSelectedSectionLabel = (currentSegmentValue: string, channelsLabel: string, regionsLabel: string) => {
  if (currentSegmentValue.startsWith(channelsSplitValue)) return channelsLabel;
  if (currentSegmentValue.startsWith(regionSplitValue)) return regionsLabel;
  return null;
};

const getMatchedLabel = (oldMenu, currentSegmentValue: string): string | null => {
  if (!oldMenu || oldMenu.isDisabled) return null;
  return oldMenu.value?.find(item => item.value === currentSegmentValue)?.label ?? null;
};

const tryUpdateMatchedSegment = (
  matchedLabel: string | null,
  newMenu,
  currentSegmentValue: string,
  updatedParams
): boolean => {
  if (!matchedLabel || !newMenu || newMenu.isDisabled) return false;

  const matchedNewItem = newMenu.value?.find(item => item.label === matchedLabel);
  if (!matchedNewItem) return false;

  if (matchedNewItem.value !== currentSegmentValue) {
    updatedParams.sgSegment = [matchedNewItem.value];
  }
  return true;
};

const ensureValidSegment = (newMenu, currentSegmentValue: string, updatedParams) => {
  const isValid = newMenu?.isDisabled ? true : newMenu?.value?.some(item => item.value === currentSegmentValue);
  if (!isValid && newMenu?.value?.[0]) {
    updatedParams.sgSegment = [newMenu.value[0].value];
  }
};

export const getCollectionsContext = (collection: {
  params: Record<string, string[]>;
}): {
  settings: SettingsValues;
  filters?: FiltersValues;
} => {
  if (collection && collection.params) {
    const filters: FiltersValues = {};

    const settings = Object.entries(collection.params).reduce((acc, [key, values]) => {
      acc[key] = values.map(item => {
        const [value, label] = item.split('|');
        return { value, label };
      });
      return acc;
    }, {} as SettingsValues);

    // Filter keys starting with 'f:' and add them to the filters object
    Object.entries(collection.params).forEach(([key, values]) => {
      if (key.startsWith('f:')) {
        filters[key] = values.map(item => {
          const [value, label] = item.split('|');
          return { value, label };
        });
      }
    });

    return {
      settings,
      filters,
    };
  }
  return null;
};

// --- Helper to fetch context settings ---
const fetchContextSettings = async ({ corePageSettings, paramsForContext }) => {
  const {
    context: { url: contextUrl },
  } = corePageSettings;
  const { settings } = await queryClient.fetchQuery({
    queryKey: ['insight-context', contextUrl, stringify(paramsForContext)],
    queryFn: () => contextClient.getContext(contextUrl, { ...paramsForContext }),
    staleTime: Infinity,
  });

  return settings;
};
